/**
 * Device Info Collector
 *
 * Gathers non-PII client-side device/environment signals to help diagnose
 * document-capture drop-off (e.g. desktop with no webcam, denied camera
 * permission, small viewports, low-memory devices). These signals are NOT
 * available server-side from the request user-agent alone.
 *
 * Every field is best-effort and guarded: this never throws and always resolves,
 * even in environments where an API is missing (older browsers, jsdom in tests).
 */

/** Frame-scoped Permissions Policy verdict for one feature. */
export type FeaturePolicyState = "allowed" | "blocked" | "unsupported";

/**
 * Which branch of iProov's motion test applies in this frame:
 * - "request-permission": DeviceMotionEvent.requestPermission exists (iOS/iPadOS).
 *   iProov calls it and requires "granted"; Permissions Policy is never consulted.
 * - "events-only": DeviceMotionEvent exists without requestPermission (Chromium,
 *   Firefox). iProov falls back to the Permissions Policy check.
 * - "unavailable": no DeviceMotionEvent at all — iProov fails the test outright.
 */
export type MotionApi = "request-permission" | "events-only" | "unavailable";

export interface MotionSupport {
  motionApi: MotionApi;
  policyAccelerometer: FeaturePolicyState;
  policyGyroscope: FeaturePolicyState;
  policyMagnetometer: FeaturePolicyState;
}

export interface DeviceInfo extends MotionSupport {
  /** See getFrameDepth(). */
  frameDepth?: number;
  userAgent: string;
  platform: string;
  language: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  /** Touch support — helps separate real device classes better than UA parsing. */
  maxTouchPoints: number;
  touchCapable: boolean;
  /** navigator.deviceMemory (GB), Chromium-only; undefined elsewhere. */
  deviceMemory?: number;
  hardwareConcurrency?: number;
  /**
   * Number of videoinput (camera) devices. 0 is the "desktop, no webcam" case
   * that can't do live capture. `undefined` = enumeration unsupported/failed.
   */
  videoInputCount?: number;
  /**
   * Number of audioinput (microphone) devices. VEX-7628: Veriff requests
   * getUserMedia({ video, audio }) in ONE call, so an unavailable mic fails
   * document capture outright and surfaces to the member as a *camera* error.
   */
  audioInputCount?: number;
  /**
   * Whether any videoinput / audioinput device reports a non-empty label. Labels
   * stay blank until access has actually been granted in this browsing context,
   * making these a no-prompt proxy for "was this really granted here" — and the
   * only zero-friction signal that still discriminates on WebKit, where
   * `*Permission` below reads "prompt" for ~95% of sessions.
   */
  videoInputLabeled?: boolean;
  audioInputLabeled?: boolean;
  /**
   * Permission state as seen from THIS FRAME: "granted" | "denied" | "prompt", or
   * "unsupported" where the Permissions API / descriptor isn't available.
   */
  cameraPermission?: string;
  micPermission?: string;
  /** navigator.connection.effectiveType (e.g. "4g"), where available. */
  connectionType?: string;
}

/**
 * Mirrors iProov's own motion support test, read out of the shipped
 * `iProovSupport.js`, WITHOUT ever prompting:
 *
 *   if (!window.DeviceMotionEvent) return false
 *   requestPermission exists
 *     ? await DeviceMotionEvent.requestPermission() === "granted"
 *     : !document.featurePolicy || ["accelerometer","gyroscope","magnetometer"]
 *         .every(f => document.featurePolicy.allowsFeature(f))
 *
 * We deliberately only check that `requestPermission` EXISTS — calling it would
 * prompt the member (and outside a user gesture it rejects), which this module
 * never does.
 *
 * Two things worth knowing when reading the output:
 *
 * 1. `document.featurePolicy` is Chromium-only. On iOS Safari and every iOS in-app
 *    browser the three policy fields read "unsupported" — i.e. on exactly the cohort
 *    reporting motion failures. `motionApi` is what discriminates there. The policy
 *    fields diagnose the Android/desktop delegation chain; `motionApi` diagnoses iOS.
 * 2. iProov treats an ABSENT featurePolicy as a pass, so "unsupported" here is not
 *    itself a failure — only "blocked" is.
 */
export function getMotionSupport(): MotionSupport {
  const policy =
    typeof document !== "undefined"
      ? (document as Document & { featurePolicy?: { allowsFeature?: (f: string) => boolean } }).featurePolicy
      : undefined;

  const readPolicy = (feature: string): FeaturePolicyState => {
    if (typeof policy?.allowsFeature !== "function") return "unsupported";
    try {
      return policy.allowsFeature(feature) ? "allowed" : "blocked";
    } catch {
      return "unsupported";
    }
  };

  const deviceMotion =
    typeof window !== "undefined"
      ? (window as Window & { DeviceMotionEvent?: { requestPermission?: unknown } }).DeviceMotionEvent
      : undefined;

  const motionApi: MotionApi = !deviceMotion
    ? "unavailable"
    : typeof deviceMotion.requestPermission === "function"
      ? "request-permission"
      : "events-only";

  return {
    motionApi,
    policyAccelerometer: readPolicy("accelerometer"),
    policyGyroscope: readPolicy("gyroscope"),
    policyMagnetometer: readPolicy("magnetometer"),
  };
}

/**
 * Frames between us and the top document: 0 = top-level, 1 = embedded once.
 * `undefined` where it can't be determined (Firefox has no ancestorOrigins).
 * >1 means an ancestor we don't control also has to delegate motion.
 */
export function getFrameDepth(): number | undefined {
  try {
    return window.location.ancestorOrigins?.length ?? (window.parent === window ? 0 : undefined);
  } catch {
    return undefined;
  }
}

/**
 * Coarse iPhone generation, mirroring iProov's own bucket. Every iPhone since the 4S has
 * the sensors, so a flat split across buckets means motion failures aren't the hardware.
 * Uses physical screen.height, so the iframe reads the same as the top level.
 */
export function getIPhoneBucket(): "8-or-older" | "X-or-newer" | "not-iphone" | "unknown" {
  try {
    if (typeof navigator === "undefined" || !/iPhone/.test(navigator.userAgent)) {
      return "not-iphone";
    }
    const height = window.screen?.height;
    if (typeof height !== "number" || height <= 0) return "unknown";
    return height <= 736 ? "8-or-older" : "X-or-newer";
  } catch {
    return "unknown";
  }
}

/**
 * iOS masquerading as macOS via "Request Desktop Website" — a Mac UA with maxTouchPoints > 1.
 * Uncorrected these contaminate desktop cohorts. Real Macs report 0, Touch Bar included.
 */
export function isDesktopModeSpoofed(): boolean {
  try {
    if (typeof navigator === "undefined") return false;
    const looksMac = /Mac ?OS/i.test(navigator.userAgent) || /^Mac/i.test(navigator.platform ?? "");
    return looksMac && (navigator.maxTouchPoints ?? 0) > 1;
  } catch {
    return false;
  }
}

/** Synchronous, always-safe base signals. */
function baseInfo(): DeviceInfo {
  const nav = typeof navigator !== "undefined" ? navigator : ({} as Navigator);
  const maxTouchPoints = typeof nav.maxTouchPoints === "number" ? nav.maxTouchPoints : 0;
  return {
    ...getMotionSupport(),
    frameDepth: getFrameDepth(),
    userAgent: nav.userAgent ?? "",
    platform: nav.platform ?? "",
    language: nav.language ?? "",
    screenWidth: typeof screen !== "undefined" ? screen.width : 0,
    screenHeight: typeof screen !== "undefined" ? screen.height : 0,
    viewportWidth: typeof window !== "undefined" ? window.innerWidth : 0,
    viewportHeight: typeof window !== "undefined" ? window.innerHeight : 0,
    devicePixelRatio: typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
    maxTouchPoints,
    touchCapable: maxTouchPoints > 0 || (typeof window !== "undefined" && "ontouchstart" in window),
    deviceMemory: (nav as Navigator & { deviceMemory?: number }).deviceMemory,
    hardwareConcurrency: typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : undefined,
    connectionType: (nav as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType,
  };
}

type MediaInputs = Pick<
  DeviceInfo,
  "videoInputCount" | "audioInputCount" | "videoInputLabeled" | "audioInputLabeled"
>;

/**
 * Counts and label-presence for both media kinds from a single enumerateDevices
 * call. Never prompts: enumerateDevices exposes device kinds without permission
 * (labels are blank until granted, but the count/kind is available).
 */
async function enumerateMediaInputs(): Promise<MediaInputs> {
  try {
    if (!navigator?.mediaDevices?.enumerateDevices) return {};
    const devices = await navigator.mediaDevices.enumerateDevices();
    const video = devices.filter((d) => d.kind === "videoinput");
    const audio = devices.filter((d) => d.kind === "audioinput");
    return {
      videoInputCount: video.length,
      audioInputCount: audio.length,
      videoInputLabeled: video.some((d) => !!d.label),
      audioInputLabeled: audio.some((d) => !!d.label),
    };
  } catch {
    return {};
  }
}

/**
 * Frame-scoped permission state for a media descriptor. Never prompts.
 *
 * See the `cameraPermission` / `micPermission` docs above for the two ways this
 * is easy to misread (frame scoping, and persistent-grant-only semantics).
 */
async function queryMediaPermission(name: "camera" | "microphone"): Promise<string | undefined> {
  try {
    if (!navigator?.permissions?.query) return "unsupported";
    // Neither name is in the TS PermissionName union in all lib versions.
    const status = await navigator.permissions.query({ name: name as PermissionName });
    return status.state;
  } catch {
    return "unsupported";
  }
}

/**
 * Collect a best-effort device snapshot. Always resolves; never throws.
 *
 * Nothing here prompts the member. VEX-7628 deliberately keeps it that way: this
 * frame's origin differs from Veriff's, so a probe that prompted would ask twice
 * and still not tell us whether Veriff can open the camera.
 */
export async function collectDeviceInfo(): Promise<DeviceInfo> {
  const info = baseInfo();
  const [inputs, cameraPermission, micPermission] = await Promise.all([
    enumerateMediaInputs(),
    queryMediaPermission("camera"),
    queryMediaPermission("microphone"),
  ]);
  return { ...info, ...inputs, cameraPermission, micPermission };
}
